<style>
	:global(#color > .mdc-floating-label) {
		left: 50px !important;
	}
</style>

<script lang="ts">
	import Accordion, { Content, Header, Panel } from '@smui-extra/accordion';
	import Button, { Label } from '@smui/button';
	import Card from '@smui/card';
	import Checkbox from '@smui/checkbox';
	import FormField from '@smui/form-field';
	import List, { Item, Separator, Text } from '@smui/list';
	import type { MenuSurfaceComponentDev } from '@smui/menu-surface';
	import MenuSurface from '@smui/menu-surface';
	import Select, { Option } from '@smui/select';
	import Input from '@smui/textfield';
	import { _ } from 'svelte-i18n';
	import { derived, writable } from 'svelte/store';
	import FieldLoading from '../../../../shared/components/common/Loading/FieldLoading.svelte';
	import FormLoading from '../../../../shared/components/common/Loading/FormLoading.svelte';
	import Title from '../../../../shared/components/common/Title.svelte';
	import ParagrafiAccessibilitaPSCL from '../../../../shared/components/mobilityPlatform/Sede/Detail/ParagrafiAccessibilitaPSCL.svelte';
	import Area from '../../../../shared/models/Area/Area';
	import AziendaEnte from '../../../../shared/models/AziendaEnte/AziendaEnte';
	import Sede from '../../../../shared/models/Sede/Sede';
	import TipologiaLavoratore from '../../../../shared/models/Sede/TipologiaLavoratore';
	import { hasRoleSysAdmin, userSession } from '../../../../shared/stores';
	import { isNumeric } from '../../../../shared/validators';
	
	export let params = {
		id: '0',
	};

	let surface: MenuSurfaceComponentDev;
	let disabled = true;
	let gestioneOreStraordinarieAltro = '';

	let [data, loading, error] = Sede.get(params.id);
	let [dataAree, loadingAree, errorAree] = $hasRoleSysAdmin ? Area.getList() : [null, writable(false), null];
	let [dataAziendeEnti, loadingAziendeEnti, errorAziendeEnti] = $hasRoleSysAdmin
		? AziendaEnte.getList()
		: [writable({ response: [] }), writable(false), null];
	let [dataAziendaEnte, loadingAziendaEnte, errorAziendaEnte] = !$hasRoleSysAdmin
		? AziendaEnte.get($userSession?.aziendaEnteId)
		: [null, writable(false), null];
	
	let selectedArea: Area = new Area();
	let selectedAziendaEnte: AziendaEnte = new AziendaEnte();
	$: selectedArea = $dataAree?.response?.find((x) => x?.id === $data?.areaId);
	$: if ($dataAziendeEnti?.response?.length > 0 && $data.areaId > 0) {
		$dataAziendeEnti.response = $dataAziendeEnti?.response?.filter((x) => x?.areaId === $data?.areaId);
		if (!$hasRoleSysAdmin) {
			if ($data.aziendaEnteId > 0) {
				[dataAziendaEnte, loadingAziendaEnte, errorAziendaEnte] = AziendaEnte.get($userSession?.aziendaEnteId);
			}
		}
	}
	$: selectedAziendaEnte = $dataAziendeEnti?.response?.find((x) => x?.id === $data?.aziendaEnteId);

	$: {
		if ($data?.sedeAltreInfo?.gestioneOreStraordinarie) {
			$data.sedeAltreInfo.gestioneOreStraordinarie = gestioneOreStraordinarieAltro
			? gestioneOreStraordinarieAltro
			: $data.sedeAltreInfo.gestioneOreStraordinarie;
		}
	}

	$: $data.numeroTotaleLavoratori = (
		($data.numeroPersonaleFullTime || 0) +
		($data.numeroPersonalePartTime || 0) +
		($data.numeroPersonaleEsterno || 0)
	);
		
	function createBoxStore() {
		return writable([]);
	}

	export let reteStradaleBoxes = createBoxStore();
	export let ciclabilitaBoxes = createBoxStore();
	export let trasportoPubblicoBoxes = createBoxStore();
	export let mobilitaCondivisaBoxes = createBoxStore();

	let reteStradaleBoxesWithByteArray = [];
	let ciclabilitaBoxesWithByteArray = [];
	let mobilitaCondivisaBoxesWithByteArray = [];
	let trasportoPubblicoBoxesWithByteArray = [];

	async function processBox(box, allegatoType) {
		let documento = [];
		if (box.file && box.file instanceof Blob) {
			const content = await getByteArray(box.file);
			if (content instanceof Uint8Array) {
				documento = Array.from(content);
			}
		} else if (box.allegato && box.allegato.documento && typeof box.allegato.documento === 'string') {
			const byteCharacters = atob(box.allegato.documento);
			const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
			documento = byteNumbers;
		}

		return {
			id: box.paragrafoId ?? 0,
			descrizione: box.text,
			[allegatoType]: box.file || box.allegato ? {
				nome: box.file?.name || box.allegato?.nome,
				nomeFile: box.file?.name || box.allegato?.nome,
				tipoAllegatoId: 0,
				documento: documento,
				mimeType: box.file?.type || box.allegato?.mimeType
			} : null
		};
	}

	async function subscribeBoxes(boxes, setByteArray, allegatoType) {
		boxes.subscribe(async value => {
			if (value.length > 0) {
				setByteArray(await Promise.all(value.map(box => processBox(box, allegatoType))));
			} else {
				setByteArray([]);
			}
		});
	}

	subscribeBoxes(reteStradaleBoxes, value => reteStradaleBoxesWithByteArray = value, 'allegatoSedeParagrafoReteStradale');
	subscribeBoxes(ciclabilitaBoxes, value => ciclabilitaBoxesWithByteArray = value, 'allegatoSedeParagrafoCiclabilita');
	subscribeBoxes(mobilitaCondivisaBoxes, value => mobilitaCondivisaBoxesWithByteArray = value, 'allegatoSedeParagrafoMobilitaCondivisa');
	subscribeBoxes(trasportoPubblicoBoxes, value => trasportoPubblicoBoxesWithByteArray = value, 'allegatoSedeParagrafoTrasportoPubblico');
	
	$: {
		if (
			!$data.denominazione ||
			!$data.localizzazione ||
			isNaN($data.numeroPersonaleFullTime) ||
			isNaN($data.numeroPersonaleEsterno) ||
			!$data.indirizzo
		) {
			disabled = true;
		} else {
			if ($hasRoleSysAdmin) {
				if (!selectedArea?.id || !selectedAziendaEnte?.id) {
					disabled = true;
				} else {
					disabled = false;
				}
			} else {
				disabled = false;
			}
			if ($dataAziendaEnte?.scuola || selectedAziendaEnte?.scuola) {
				if (!$data.ordineGradoSedeScuola || isNaN($data.numeroStudenti)) {
					disabled = true;
				} else {
					disabled = false;
				}
			} else {
				if (
					isNaN($data.numeroPersonaleFullTimeDonne) || 
					isNaN($data.numeroPersonaleFullTimeUomini) || 
					isNaN($data.numeroPersonalePartTime) ||
					isNaN($data.numeroPersonalePartTimeDonne) || 
					isNaN($data.numeroPersonalePartTimeUomini) || 
					!$data.formePartTime
				) {
					disabled = true;
				} else {
					disabled = false;
				}
			}
		}
	}

	$: {
		if ($data && $data.id > 0) {
			const setBoxes = (source, setter, allegatoKey) => {
				if (source) {
					setter(source.map(paragrafo => ({
						id: paragrafo.id,
						paragrafoId: paragrafo.id,
						text: paragrafo.descrizione,
						image: paragrafo?.[allegatoKey]?.mimeType && paragrafo?.[allegatoKey]?.documento 
							? `data:${paragrafo[allegatoKey].mimeType};base64,${paragrafo[allegatoKey].documento}` 
							: '',
						file: null,
						allegato: paragrafo?.[allegatoKey] ?? null,
						documento: paragrafo?.[allegatoKey]?.documento ?? null
					})));
				} else {
					setter([]);
				}
			};

			setBoxes($data?.sedeParagrafiAccessibilitaPSCL?.sedeParagrafiReteStradale, reteStradaleBoxes.set, 'allegatoSedeParagrafoReteStradale');
			setBoxes($data?.sedeParagrafiAccessibilitaPSCL?.sedeParagrafiCiclabilita, ciclabilitaBoxes.set, 'allegatoSedeParagrafoCiclabilita');
			setBoxes($data?.sedeParagrafiAccessibilitaPSCL?.sedeParagrafiMobilitaCondivisa, mobilitaCondivisaBoxes.set, 'allegatoSedeParagrafoMobilitaCondivisa');
			setBoxes($data?.sedeParagrafiAccessibilitaPSCL?.sedeParagrafiTrasportoPubblico, trasportoPubblicoBoxes.set, 'allegatoSedeParagrafoTrasportoPubblico');
		}
	}

	function resetDataForSchool($data) {
		const { 
			sedeDotazioniServizi, 
			sedeStrumentiComunicazione, 
			sedeAltreInfo 
		} = $data;
		
		return {
			...$data,
			numeroPersonaleFullTimeDonne: 0,
			numeroPersonaleFullTimeUomini: 0,
			numeroPersonalePartTime: 0,
			numeroPersonalePartTimeDonne: 0,
			numeroPersonalePartTimeUomini: 0,
			formePartTime: '',
			numeroTotaleLavoratori: ($data.numeroPersonaleFullTime || 0) + ($data.numeroPersonaleEsterno || 0),
			sedeDotazioniServizi: {
				...sedeDotazioniServizi,
				navettaAziendale: false,
				navettaAziendaleNote: '',
				autoAziendali: false,
				autoAziendaliNote: '',
				motoBikeMonAziendali: false,
				motoBikeMonAziendaliNote: '',
				carSharingAziendale: false,
				carSharingAziendaleNote: '',
				incentiviScontiTrasportoPub: false,
				incentiviScontiTrasportoPubNote: '',
				incentiviScontiMobilitaCondivisa: false,
				incentiviScontiMobilitaCondivisaNote: '',
				incentiviUsoBike: false,
				incentiviUsoBikeNote: '',
				smartWorkingTelelavoro: false,
				smartWorkingTelelavoroNote: '',
				smartWorkingTelelavoroOre: 0,
				smartWorkingTelelavoroRequisiti: '',
				smartWorkingTelelavoroStrumenti: '',
				piattaformaCarPooling: false,
				piattaformaCarPoolingNote: '',
				postiAutoCarPooling: 0,
				numeroPostiAutoCarPooling: 0,
				postiAutoCarPoolingNote: '',
				incentiviScontiCarPooling: false,
				incentiviScontiCarPoolingNote: '',
				spogliatoiDocce: false,
				spogliatoiDocceNote: '',
				mensaAziendale: false,
				mensaAziendaleNote: '',
				asiloAziendale: false,
				asiloAziendaleNote: '',
				altriServizi: false,
				altriServiziNote: ''
			},
			sedeStrumentiComunicazione: {
				...sedeStrumentiComunicazione,
				volantiniBrochure: false,
				affissioni: false,
				rubricaGiornaleAzienda: false,
				emailAllUser: false,
				campagneSocial: false,
				newsletter: false,
				sitoIntranet: false,
				altro: false
			},
			sedeAltreInfo: {
				...sedeAltreInfo,
				budgetAnnoEconomicoMobilità: 0,
				risorseUmaneDedicateMobilità: 0,
				sabatoLavorativo: false,
				domenicaLavorativo: false,
				orarioInDa: '',
				orarioInA: '',
				orarioOutDa: '',
				orarioOutA: '',
				altriOrari: false,
				gestioneOreStraordinarie: ''
			}
		};
	}

	function getByteArray(file) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
			const byteArray = new Uint8Array(reader.result as ArrayBuffer);
				resolve(byteArray);
			};
			reader.onerror = error => reject(error);
			reader.readAsArrayBuffer(file);
		});
	}

	async function onSubmit() {
		$data.scuola = $dataAziendaEnte?.scuola || selectedAziendaEnte?.scuola ? true : false;
		if ($data.scuola) {
			$data = resetDataForSchool($data);
		} else {
			$data.sedeTipologieLavoratori = sedeTipologieLavoratori;
		}

		const sedeData = {
			...$data,
			sedeParagrafiAccessibilitaPSCL: {
				...$data.sedeParagrafiAccessibilitaPSCL,
				sedeParagrafiReteStradale: reteStradaleBoxesWithByteArray,
				sedeParagrafiCiclabilita: ciclabilitaBoxesWithByteArray,
				sedeParagrafiMobilitaCondivisa: mobilitaCondivisaBoxesWithByteArray,
				sedeParagrafiTrasportoPubblico: trasportoPubblicoBoxesWithByteArray
			}
		};
		[data, loading, error] = Sede.save(sedeData);
	}

	let sedeTipologieLavoratori = [];

	function findSedeTipologiaLavoratore(tipologiaLavoratore) {
        let sedeTipologiaLavoratore = $data.sedeTipologieLavoratori.find(s => s.tipologiaLavoratoreId === tipologiaLavoratore.id);
        if (!sedeTipologiaLavoratore) {
            sedeTipologiaLavoratore = {
				id: 0,
                sedeId: Number(params.id), 
                tipologiaLavoratoreId: tipologiaLavoratore.id,
				tipologiaLavoratore: $dataTipologieLavoratoriGenerali?.response?.find(t => t.id === tipologiaLavoratore.id),
                percentuale: 0
            };
        }
		sedeTipologieLavoratori.push(sedeTipologiaLavoratore); 
        return sedeTipologiaLavoratore;
    }

    function getTipologiaWithSede(tipologiaLavoratore) {
        const sedeTipologiaLavoratore = findSedeTipologiaLavoratore(tipologiaLavoratore);
        return sedeTipologiaLavoratore;
    }

	let [dataTipologieLavoratoriGenerali, loadingTipologieLavoratoriGenerali, errorTipologieLavoratoriGenerali] = 
			params.id === '0' ? 
			Sede.getListTipologieLavoratoriGenerali() : 
			[writable({ response: [] }), writable(false), null];

		const dataAndTipologieLavoratoriGenerali = derived(
			[loading, loadingTipologieLavoratoriGenerali], 
			([$loading, $loadingTipologieLavoratoriGenerali]) => {
				if (!$loading && !$loadingTipologieLavoratoriGenerali) {
					if (params.id === '0') {
						const tipologieLavoratoriGeneraliIds = $dataTipologieLavoratoriGenerali.response.map(t => t.id);
						const sedeTipologieLavoratoriFiltered = $data.sedeTipologieLavoratori.filter(s => !tipologieLavoratoriGeneraliIds.includes(s.tipologiaLavoratoreId));
						return [
							...sedeTipologieLavoratoriFiltered,
							...$dataTipologieLavoratoriGenerali.response.map(getTipologiaWithSede)
						];
					} else {
						return $data.sedeTipologieLavoratori || [];
					}
				}
				else {
					return [];
				}
			}
		);

	dataAndTipologieLavoratoriGenerali.subscribe(value => {
		sedeTipologieLavoratori = value;
	});

	function createNewSedeTipologiaLavoratore() {
		return {
			id: 0,
			sedeId: Number(params.id),
			tipologiaLavoratoreId: 0,
			tipologiaLavoratore: {
				id: 0,
				nome: '',
				sedeId: Number(params.id),
			},
			percentuale: 0,
		};
	}

	let newSedeTipologiaLavoratore = createNewSedeTipologiaLavoratore();
	
	function addTipologiaLavoratore() {
		const nuovaTipologia = {
			...newSedeTipologiaLavoratore,
			tipologiaLavoratore: {
				...newSedeTipologiaLavoratore.tipologiaLavoratore
			}
		};
		
		sedeTipologieLavoratori = [...sedeTipologieLavoratori, nuovaTipologia];
		
		newSedeTipologiaLavoratore = createNewSedeTipologiaLavoratore();
	}

	function deleteTipologiaLavoratore(index) {
		sedeTipologieLavoratori.splice(index, 1);
		sedeTipologieLavoratori = [...sedeTipologieLavoratori];
	}
</script>

<Card class="md:px-8 md:py-8" padded>
	<div class="flex justify-between">
		<Title title="{'Sede'}" />
		<div class="sm:flex-nowrap sm:space-x-4 flex flex-wrap">
			<button type="submit" form="form" class="btn-white h-14 w-14 sm:h-16 sm:w-16" disabled="{disabled}">
				<span class="material-icons-outlined sm:text-3xl text-2xl">save</span>
			</button>
		</div>
	</div>
	{#if $loading}
		<FormLoading />
	{:else}
		<div class="font-bold text-center text-red-600">
			ATTENZIONE: Salvare prima di uscire
		</div>
		<div class="pt-4">
			<Card class="md:px-8 md:py-8 pt-4" padded>
				<div class="flex justify-between">
					<Title title="{$_('page.locations.detail.basic_informations')}" inCard="{true}" />
				</div>
				<form on:submit|preventDefault="{onSubmit}" id="form">
					{#if $hasRoleSysAdmin}
						<div class="md:grid-cols-2 md:gap-x-8 md:px-4 lg:grid-cols-2 grid grid-cols-1 pt-4">
							{#if $loadingAree}
								<FieldLoading />
							{:else}
								<Select
									required
									key="{(area) => `${(area && area.id) || ''}`}"
									label="{$_('page.locations.detail.area')}"
									bind:value="{selectedArea}"
								>
									{#if $dataAree.response}
										{#each $dataAree.response as area (area.id)}
											{#if area.nomeComune}
												<Option
													value="{area}"
													on:click="{() => {
														$data.area = area;
														$data.areaId = area.id;
														[dataAziendeEnti, loadingAziendeEnti, errorAziendeEnti] = AziendaEnte.getList({
															areaId: area.id,
														});
													}}">{area.nomeComune}</Option
												>
											{/if}
										{/each}
									{/if}
								</Select>
							{/if}
							{#if $loadingAziendeEnti}
								<FieldLoading />
							{:else}
								<Select
									disabled="{!selectedArea?.id}"
									required
									key="{(aziendaEnte) => `${(aziendaEnte && aziendaEnte.id) || ''}`}"
									label="{$_('page.locations.detail.company/entity')}"
									bind:value="{selectedAziendaEnte}"
								>
									{#if $dataAziendeEnti && $dataAziendeEnti.response}
										{#each $dataAziendeEnti.response as aziendaEnte (aziendaEnte.id)}
											{#if aziendaEnte.nomeAziendaEnte}
												<Option
													value="{aziendaEnte}"
													on:click="{() => {
														$data.aziendaEnte = aziendaEnte;
														$data.aziendaEnteId = aziendaEnte.id;
													}}">{aziendaEnte.nomeAziendaEnte}</Option
												>
											{/if}
										{/each}
									{/if}
								</Select>
							{/if}
						</div>
					{/if}
					<div class="md:grid-cols-2 md:gap-x-8 md:px-4 grid grid-cols-1 pt-4">
						<Input required label="{$_('page.locations.detail.denomination')}" bind:value="{$data.denominazione}" />
						<Select required bind:value="{$data.localizzazione}" label="{$_('page.locations.detail.localization')}">
							<Option value="Urbana centrale">{$_('page.locations.detail.localization_central_urban_option')}</Option>
							<Option value="Urbana periferica">{$_('page.locations.detail.localization_peripheral_urban_option')}</Option>
							<Option value="Extraurbana">{$_('page.locations.detail.localization_extra_urban_option')}</Option>
						</Select>
					</div>
					{#if $dataAziendaEnte?.scuola || selectedAziendaEnte?.scuola}
						<div class="md:grid-cols-2 md:gap-x-8 md:px-4 grid grid-cols-1 pt-4">
							<Select
								required
								bind:value="{$data.ordineGradoSedeScuola}"
								label="{$_('page.locations.detail.order_grade_school_location')}"
							>
								<Option value="Primaria">{$_('page.locations.detail.order_grade_school_location_primary_option')}</Option>
								<Option value="Secondaria di I grado"
									>{$_('page.locations.detail.order_grade_school_location_secondary_option')}</Option
								>
								<Option value="Secondaria di II grado"
									>{$_('page.locations.detail.order_grade_school_location_upper_secondary_option')}</Option
								>
							</Select>
							<Input
								label="{$_('page.locations.detail.number_students')}"
								bind:value="{$data.numeroStudenti}"
								type="number"
								input$step="1"
								input$min="0"
							/>
						</div>
					{/if}
					<div class="md:px-4 grid grid-cols-1 pt-4">
						<Input
							label="{$_('page.locations.detail.total_number_of_workers')}"
							bind:value="{$data.numeroTotaleLavoratori}"
							type="number"
							input$step="1"
							input$min="0"
							disabled
						/>
					</div>
					<div class="md:px-4 grid grid-cols-1 pt-4">
						<Input
							required
							label="{$_('page.locations.detail.number_full-time_staff')}"
							bind:value="{$data.numeroPersonaleFullTime}"
							type="number"
							input$step="1"
							input$min="0"
						/>
					</div>
					<div class="md:grid-cols-3 md:gap-x-8 md:px-4 grid grid-cols-1 pt-4">
						{#if !$dataAziendaEnte?.scuola && !selectedAziendaEnte?.scuola}
							<Input
								required
								label="{$_('page.locations.detail.of_whom_women')}"
								bind:value="{$data.numeroPersonaleFullTimeDonne}"
								type="number"
								input$step="0.01"
								input$min="0"
								input$max="100"
							>
								<span slot="trailingIcon" class="p-2.5">%</span>
							</Input>
							<Input
								required
								label="{$_('page.locations.detail.of_whom_men')}"
								bind:value="{$data.numeroPersonaleFullTimeUomini}"
								type="number"
								input$step="0.01"
								input$min="0"
								input$max="100"
							>
							<span slot="trailingIcon" class="p-2.5">%</span>
							</Input>
						{/if}
						<Input
							required
							label="{$_('page.locations.detail.number_hours_contract_full-time')}"
							bind:value="{$data.numeroOreContrattoFullTime}"
							type="number"
							input$step="0.01"
							input$min="0"
						/>
					</div>
					{#if !$dataAziendaEnte?.scuola && !selectedAziendaEnte?.scuola}
						<div class="md:px-4 grid grid-cols-1 pt-4">
							<Input
								required
								label="{$_('page.locations.detail.number_staff_part-time')}"
								bind:value="{$data.numeroPersonalePartTime}"
								type="number"
								input$step="1"
								input$min="0"
							/>
						</div>
						<div class="md:grid-cols-3 md:gap-x-8 md:px-4 grid grid-cols-1 pt-4">
							<Input
								required
								label="{$_('page.locations.detail.of_whom_women')}"
								bind:value="{$data.numeroPersonalePartTimeDonne}"
								type="number"
								input$step="0.01"
								input$min="0"
								input$max="100"
							>
								<span slot="trailingIcon" class="p-2.5">%</span>
							</Input>
							<Input
								required
								label="{$_('page.locations.detail.of_whom_men')}"
								bind:value="{$data.numeroPersonalePartTimeUomini}"
								type="number"
								input$step="0.01"
								input$min="0"
								input$max="100"
							>
								<span slot="trailingIcon" class="p-2.5">%</span>
							</Input>
							<Input required label="{$_('page.locations.detail.forms_of_part-time')}" bind:value="{$data.formePartTime}" />
						</div>
					{/if}
					<div class="md:grid-cols-2 md:gap-x-8 md:px-4 grid grid-cols-1 pt-4">
						<Input
							required
							label="{$_('page.locations.detail.number_external_staff')}"
							bind:value="{$data.numeroPersonaleEsterno}"
							type="number"
							input$step="1"
							input$min="0"
						/>
						{#if !$dataAziendaEnte?.scuola && !selectedAziendaEnte?.scuola}
							<Input
								label="{$_('page.locations.detail.itinerant_workers')}"
								bind:value="{$data.lavoratoriItineranti}"
							/>
						{/if}
					</div>
					<div class="md:grid-cols-3 md:gap-x-8 md:px-4 grid grid-cols-1 pt-4">
						<Input label="{$_('page.locations.detail.province')}" bind:value="{$data.provincia}" input$max="2" />
						<Input label="{$_('page.locations.detail.city')}" bind:value="{$data.citta}" />
						<Input
							label="{$_('page.locations.detail.postal_code')}"
							bind:value="{$data.cap}"
							input$maxlength="{5}"
							on:keypress="{isNumeric}"
						/>
					</div>
					<div class="md:px-4 grid grid-cols-1 pt-4">
						<Input required label="{$_('page.locations.detail.address')}" bind:value="{$data.indirizzo}">
							<svelte:fragment slot="trailingIcon">
								{#if $data.id > 0}
									{#if $data.provincia && $data.citta && $data.cap && $data.indirizzoOK}
										<span title="{$_('page.locations.detail.valid_address')}" class="material-icons-outlined" style="color: green"
											>fmd_good</span
										>
									{:else}
										<span
											title="{$_('page.locations.detail.not_valid_address')}"
											class="material-icons-outlined"
											style="color: red">wrong_location</span
										>
									{/if}
								{/if}
							</svelte:fragment>
						</Input>
					</div>
					{#if !$dataAziendaEnte?.scuola && !selectedAziendaEnte?.scuola}
						<div class="accordion-container pt-4">
							<Accordion>
								<Panel>
									<Header>{$_('page.locations.detail.workers_types')}</Header>
									<Content>
										<div class="gap-y-2 grid grid-cols-1">
											{#if sedeTipologieLavoratori?.length > 0}
												{#each sedeTipologieLavoratori as sedeTipologiaLavoratore, index (index)}
													<div class="flex gap-4 p-2.5 border border-black">
														<FormField class="flex-1">
															<span slot="label">{sedeTipologiaLavoratore.tipologiaLavoratore?.nome}</span>
														</FormField>
														<Input
															bind:value="{sedeTipologiaLavoratore.percentuale}"
															type="number"
															input$step="0.01"
															input$min="0"
															input$max="100"
															class="flex-1"
															required
														>
															<span slot="trailingIcon" class="p-2.5">%</span>
														</Input>
														<div class="flex items-center justify-center">
															<button type="button" class="h-7.5 w-7.5 bg-mobilityPlatform-green" on:click="{() => deleteTipologiaLavoratore(index)}">
																<span class="material-icons-outlined text-base leading-[1.8rem]">delete</span>
															</button>
														</div>														  
													</div>
												{/each}
											{/if}
										</div>
										<div class="grid grid-cols-1 p-2.5 gap-y-2">
											<div class="grid grid-cols-1 p-2.5 border border-black lg:grid-cols-2 lg:gap-x-2">
												<Input bind:value="{newSedeTipologiaLavoratore.tipologiaLavoratore.nome}" label="{$_('global.other')}"/>
												<Input
													bind:value="{newSedeTipologiaLavoratore.percentuale}"
													type="number"
													input$step="0.01"
													input$min="0"
													input$max="100"
												>
													<span slot="trailingIcon" class="p-2.5">%</span>
												</Input>
												<div class="lg:justify-self-end col-span-3 pt-2">
													<Button disabled={!newSedeTipologiaLavoratore.tipologiaLavoratore.nome || isNaN(newSedeTipologiaLavoratore.percentuale) || typeof newSedeTipologiaLavoratore.percentuale === 'undefined'} type="button" variant="raised" on:click="{addTipologiaLavoratore}">
														<Label>{$_('global.add')}</Label>
													</Button>
												</div>
											</div>
										</div>
									</Content>
								</Panel>
							</Accordion>
						</div>
					{/if}
				</form>
			</Card>
		</div>
		<div class="pt-4">
			<Card class="md:px-8 md:py-8 pt-4" padded>
				<div class="flex justify-between">
					<Title title="{$_('page.locations.detail.accessibility_profile')}" inCard="{true}" />
				</div>
				<div class="accordion-container pt-4">
					<Accordion>
						<Panel>
							<Header>{$_('page.locations.detail.offer_transportation')}</Header>
							<Content>
								<div class="grid grid-cols-1">
									<Select
										label="{$_('page.locations.detail.interchange_nodes')}"
										bind:value="{$data.sedeOffertaTrasporti.nodiInterscambio}"
									>
										<Option value="Non presenti">{$_('page.locations.detail.not_present_option')}</Option>
										<Option value="Molto bassa">{$_('page.locations.detail.very_low_option')}</Option>
										<Option value="Bassa">{$_('page.locations.detail.low_option')}</Option>
										<Option value="Alta">{$_('page.locations.detail.high_option')}</Option>
										<Option value="Molto alta">{$_('page.locations.detail.very_high_option')}</Option>
									</Select>
									<Select
										label="{$_('page.locations.detail.train_stations')}"
										bind:value="{$data.sedeOffertaTrasporti.stazioniFerroviare}"
									>
										<Option value="Non presenti">{$_('page.locations.detail.not_present_option')}</Option>
										<Option value="Molto bassa">{$_('page.locations.detail.very_low_option')}</Option>
										<Option value="Bassa">{$_('page.locations.detail.low_option')}</Option>
										<Option value="Alta">{$_('page.locations.detail.high_option')}</Option>
										<Option value="Molto alta">{$_('page.locations.detail.very_high_option')}</Option>
									</Select>
									<Select
										label="{$_('page.locations.detail.metro_stations')}"
										bind:value="{$data.sedeOffertaTrasporti.stazioniMetro}"
									>
										<Option value="Non presenti">{$_('page.locations.detail.not_present_option')}</Option>
										<Option value="Molto bassa">{$_('page.locations.detail.very_low_option')}</Option>
										<Option value="Bassa">{$_('page.locations.detail.low_option')}</Option>
										<Option value="Alta">{$_('page.locations.detail.high_option')}</Option>
										<Option value="Molto alta">{$_('page.locations.detail.very_high_option')}</Option>
									</Select>
									<Select
										label="{$_('page.locations.detail.bus/trolleybus/streetcar_stops')}"
										bind:value="{$data.sedeOffertaTrasporti.fermateBus}"
									>
										<Option value="Non presenti">{$_('page.locations.detail.not_present_option')}</Option>
										<Option value="Molto bassa">{$_('page.locations.detail.very_low_option')}</Option>
										<Option value="Bassa">{$_('page.locations.detail.low_option')}</Option>
										<Option value="Alta">{$_('page.locations.detail.high_option')}</Option>
										<Option value="Molto alta">{$_('page.locations.detail.very_high_option')}</Option>
									</Select>
									<Select
										label="{$_('page.locations.detail.bicycle/cycle_paths')}"
										bind:value="{$data.sedeOffertaTrasporti.pisteCiclabili}"
									>
										<Option value="Non presenti">{$_('page.locations.detail.not_present_option')}</Option>
										<Option value="Molto bassa">{$_('page.locations.detail.very_low_option')}</Option>
										<Option value="Bassa">{$_('page.locations.detail.low_option')}</Option>
										<Option value="Alta">{$_('page.locations.detail.high_option')}</Option>
										<Option value="Molto alta">{$_('page.locations.detail.very_high_option')}</Option>
									</Select>
									<Select
										key="{(parcheggioEsternoPagamentoOption) => `${parcheggioEsternoPagamentoOption || '0' || ''}`}"
										label="{$_('page.locations.detail.parking_pricing_outside_company')}"
										bind:value="{$data.sedeOffertaTrasporti.parcheggioEsternoPagamento}"
									>
										<Option value="{1}">{$_('page.locations.detail.parking_pricing_outside_company_yes_option')}</Option>
										<Option value="{'0'}">{$_('page.locations.detail.parking_pricing_outside_company_no_option')}</Option>
										<Option value="{2}">{$_('page.locations.detail.parking_pricing_outside_company_nostop_option')}</Option>
									</Select>
								</div>
								<div class="md:grid-cols-2 md:gap-x-8 md:px-4 lg:grid-cols-3 grid grid-cols-1 pt-4">
									<FormField>
										<Checkbox bind:checked="{$data.sedeOffertaTrasporti.areaPedonaleZTL}" />
										<span slot="label">{$_('page.locations.detail.pedestrian_area/ltz')}</span>
									</FormField>
									<FormField>
										<Checkbox bind:checked="{$data.sedeOffertaTrasporti.carSharing}" />
										<span slot="label">{$_('page.locations.detail.car_sharing')}</span>
									</FormField>
									<FormField>
										<Checkbox bind:checked="{$data.sedeOffertaTrasporti.scooterSharing}" />
										<span slot="label">{$_('page.locations.detail.scooter_sharing')}</span>
									</FormField>
									<FormField>
										<Checkbox bind:checked="{$data.sedeOffertaTrasporti.bikeSharing}" />
										<span slot="label">{$_('page.locations.detail.bike_sharing')}</span>
									</FormField>
									<FormField>
										<Checkbox bind:checked="{$data.sedeOffertaTrasporti.monopattiniSharing}" />
										<span slot="label">{$_('page.locations.detail.kick_scooter_sharing')}</span>
									</FormField>
									<FormField>
										<Checkbox bind:checked="{$data.sedeOffertaTrasporti.parcheggioAziendale}" />
										<span slot="label">{$_('page.locations.detail.company_parking')}</span>
									</FormField>
								</div>
							</Content>
						</Panel>
						<Panel>
							<Header>{$_('page.locations.detail.resources')}</Header>
							<Content>
								<div class="gap-y-2 grid grid-cols-1">
									{#if !$dataAziendaEnte?.scuola && !selectedAziendaEnte?.scuola}
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.navettaAziendale}" />
												<span slot="label">{$_('page.locations.detail.company_shuttle')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.navettaAziendale}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.navettaAziendaleNote}"
											/>
										</div>
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.autoAziendali}" />
												<span slot="label">{$_('page.locations.detail.company_cars')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.autoAziendali}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.autoAziendaliNote}"
											/>
										</div>
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.motoBikeMonAziendali}" />
												<span slot="label">{$_('page.locations.detail.company_motorcycles/bicycles/kick_scooters')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.motoBikeMonAziendali}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.motoBikeMonAziendaliNote}"
											/>
										</div>
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.carSharingAziendale}" />
												<span slot="label">{$_('page.locations.detail.company_car_sharing')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.carSharingAziendale}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.carSharingAziendaleNote}"
											/>
										</div>
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.incentiviScontiTrasportoPub}" />
												<span slot="label">{$_('page.locations.detail.public_transportation_incentives')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.incentiviScontiTrasportoPub}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.incentiviScontiTrasportoPubNote}"
											/>
										</div>
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.incentiviScontiMobilitaCondivisa}" />
												<span slot="label">{$_('page.locations.detail.shared_mobility_services_incentives')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.incentiviScontiMobilitaCondivisa}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.incentiviScontiMobilitaCondivisaNote}"
											/>
										</div>
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.incentiviUsoBike}" />
												<span slot="label">{$_('page.locations.detail.bike_to_work')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.incentiviUsoBike}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.incentiviUsoBikeNote}"
											/>
										</div>
										<div class="grid grid-cols-1 p-2 border border-black">
											<div class="md:grid-cols-2 grid grid-cols-1">
												<FormField>
													<Checkbox bind:checked="{$data.sedeDotazioniServizi.smartWorkingTelelavoro}" />
													<span slot="label">{$_('page.locations.detail.smart_working')}</span>
												</FormField>
												<Input
													disabled="{!$data.sedeDotazioniServizi.smartWorkingTelelavoro}"
													label="{$_('page.locations.detail.note')}"
													bind:value="{$data.sedeDotazioniServizi.smartWorkingTelelavoroNote}"
												/>
											</div>
											<Input
												disabled="{!$data.sedeDotazioniServizi.smartWorkingTelelavoro}"
												label="{$_('page.locations.detail.smart_working_hours')}"
												bind:value="{$data.sedeDotazioniServizi.smartWorkingTelelavoroOre}"
											/>
											<Input
												disabled="{!$data.sedeDotazioniServizi.smartWorkingTelelavoro}"
												label="{$_('page.locations.detail.smart_working_requirements')}"
												bind:value="{$data.sedeDotazioniServizi.smartWorkingTelelavoroRequisiti}"
											/>
											<Select
												disabled="{!$data.sedeDotazioniServizi.smartWorkingTelelavoro}"
												label="{$_('page.locations.detail.smart_working_tools')}"
												bind:value="{$data.sedeDotazioniServizi.smartWorkingTelelavoroStrumenti}"
											>
												<Option value="Sì">{$_('page.locations.detail.smart_working_tools_yes_option')}</Option>
												<Option value="In parte">{$_('page.locations.detail.smart_working_tools_no_option')}</Option>
												<Option value="No, vengono utilizzati quelli personali del dipendente">{$_('page.locations.detail.smart_working_tools_no_employees_personal_ones_are_used_option')}</Option
												>
											</Select>
										</div>
									{/if}
									<div class="md:grid-cols-3 grid grid-cols-1 gap-2 p-2 border border-black">
										<FormField>
											<Checkbox bind:checked="{$data.sedeDotazioniServizi.postiAutoDipendenti}" />
											<span slot="label">{$_('page.locations.detail.employees_car_parking_spaces')}</span>
										</FormField>
										<Input
											disabled="{!$data.sedeDotazioniServizi.postiAutoDipendenti}"
											label="{$_('page.locations.detail.number_spaces')}"
											type="number"
											input$step="1"
											bind:value="{$data.sedeDotazioniServizi.numeroPostiAutoDipendenti}"
										/>
										<Input
											disabled="{!$data.sedeDotazioniServizi.postiAutoDipendenti}"
											label="{$_('page.locations.detail.note')}"
											bind:value="{$data.sedeDotazioniServizi.postiAutoDipendentiNote}"
										/>
									</div>
									<div class="md:grid-cols-3 grid grid-cols-1 gap-2 p-2 border border-black">
										<FormField>
											<Checkbox bind:checked="{$data.sedeDotazioniServizi.postiMotoDipendenti}" />
											<span slot="label">{$_('page.locations.detail.employees_motorcycle_parking_spaces')}</span>
										</FormField>
										<Input
											disabled="{!$data.sedeDotazioniServizi.postiMotoDipendenti}"
											label="{$_('page.locations.detail.number_spaces')}"
											type="number"
											input$step="1"
											bind:value="{$data.sedeDotazioniServizi.numeroPostiMotoDipendenti}"
										/>
										<Input
											disabled="{!$data.sedeDotazioniServizi.postiMotoDipendenti}"
											label="{$_('page.locations.detail.note')}"
											bind:value="{$data.sedeDotazioniServizi.postiMotoDipendentiNote}"
										/>
									</div>
									<div class="grid grid-cols-1 p-2 border border-black">
										<div class="md:grid-cols-3 grid grid-cols-1 gap-2">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.postiBikeDipendenti}" />
												<span slot="label">{$_('page.locations.detail.employees_bike_parking_spaces')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.postiBikeDipendenti}"
												label="{$_('page.locations.detail.number_spaces')}"
												type="number"
												input$step="1"
												bind:value="{$data.sedeDotazioniServizi.numeroPostiBikeDipendenti}"
											/>
											<Input
												disabled="{!$data.sedeDotazioniServizi.postiBikeDipendenti}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.postiBikeDipendentiNote}"
											/>
										</div>
										<Select
											disabled="{!$data.sedeDotazioniServizi.postiBikeDipendenti}"
											label="{$_('page.locations.detail.bike_parking_type')}"
											bind:value="{$data.sedeDotazioniServizi.postiBikeTipologia}"
										>
											<Option value="Rastrelliera scoperta">{$_('page.locations.detail.bike_parking_type_rack_option')}</Option>
											<Option value="Rastrelliera coperta">{$_('page.locations.detail.bike_parking_type_covered_rack_option')}</Option>
											<Option value="Posteggio al chiuso">{$_('page.locations.detail.bike_parking_type_indoor_option')}</Option>
											<Option value="Posteggio al chiuso videosorvegliato">{$_('page.locations.detail.bike_parking_type_indoor_video_surveillance_option')}</Option>
											<Option value="Altro">{$_('page.locations.detail.bike_parking_type_other_option')}</Option>
										</Select>
									</div>
									{#if !$dataAziendaEnte?.scuola && !selectedAziendaEnte?.scuola}
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.piattaformaCarPooling}" />
												<span slot="label">{$_('page.locations.detail.company_car_pooling')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.piattaformaCarPooling}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.piattaformaCarPoolingNote}"
											/>
										</div>
										<div class="md:grid-cols-3 grid grid-cols-1 gap-2 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.postiAutoCarPooling}" />
												<span slot="label">{$_('page.locations.detail.car_pooling_parking_spaces')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.postiAutoCarPooling}"
												label="{$_('page.locations.detail.number_spaces')}"
												type="number"
												input$step="1"
												bind:value="{$data.sedeDotazioniServizi.numeroPostiAutoCarPooling}"
											/>
											<Input
												disabled="{!$data.sedeDotazioniServizi.postiAutoCarPooling}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.postiAutoCarPoolingNote}"
											/>
										</div>
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.incentiviScontiCarPooling}" />
												<span slot="label">{$_('page.locations.detail.car_pooling_incentives')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.incentiviScontiCarPooling}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.incentiviScontiCarPoolingNote}"
											/>
										</div>
									{/if}
									<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
										<FormField>
											<Checkbox bind:checked="{$data.sedeDotazioniServizi.depositoMonopattini}" />
											<span slot="label">{$_('page.locations.detail.kick_scooter_storage_area')}</span>
										</FormField>
										<Input
											disabled="{!$data.sedeDotazioniServizi.depositoMonopattini}"
											label="{$_('page.locations.detail.note')}"
											bind:value="{$data.sedeDotazioniServizi.depositoMonopattiniNote}"
										/>
									</div>
									<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
										<FormField>
											<Checkbox bind:checked="{$data.sedeDotazioniServizi.bikeScooterChargingPoints}" />
											<span slot="label">{$_('page.locations.detail.bike_scooter_charging_points')}</span>
										</FormField>
										<Input
											disabled="{!$data.sedeDotazioniServizi.bikeScooterChargingPoints}"
											label="{$_('page.locations.detail.note')}"
											bind:value="{$data.sedeDotazioniServizi.bikeScooterChargingPointsNote}"
										/>
									</div>
									{#if !$dataAziendaEnte?.scuola && !selectedAziendaEnte?.scuola}
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.spogliatoiDocce}" />
												<span slot="label">{$_('page.locations.detail.changing_rooms_with_showers')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.spogliatoiDocce}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.spogliatoiDocceNote}"
											/>
										</div>
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.mensaAziendale}" />
												<span slot="label">{$_('page.locations.detail.company_canteen')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.mensaAziendale}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.mensaAziendaleNote}"
											/>
										</div>
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.asiloAziendale}" />
												<span slot="label">{$_('page.locations.detail.company_kindergarten')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.asiloAziendale}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.asiloAziendaleNote}"
											/>
										</div>
										<div class="md:grid-cols-2 grid grid-cols-1 p-2 border border-black">
											<FormField>
												<Checkbox bind:checked="{$data.sedeDotazioniServizi.altriServizi}" />
												<span slot="label">{$_('page.locations.detail.company_other_services')}</span>
											</FormField>
											<Input
												disabled="{!$data.sedeDotazioniServizi.altriServizi}"
												label="{$_('page.locations.detail.note')}"
												bind:value="{$data.sedeDotazioniServizi.altriServiziNote}"
											/>
										</div>
									{/if}
								</div>
							</Content>
						</Panel>
						{#if !$dataAziendaEnte?.scuola && !selectedAziendaEnte?.scuola}
							<Panel>
								<Header>{$_('page.locations.detail.other_informations')}</Header>
								<Content>
									<div class="gap-y-2 md:grid-cols-4 md:gap-x-8 md:gap-y-0 md:px-4 grid grid-cols-1">
										<Input
											bind:value="{$data.sedeAltreInfo.orarioInDa}"
											label="{$_('page.locations.detail.employees_entrance_time_from')}"
											type="time"
										/>
										<Input
											bind:value="{$data.sedeAltreInfo.orarioInA}"
											label="{$_('page.locations.detail.employees_entrance_time_to')}"
											type="time"
										/>
										<Input
											bind:value="{$data.sedeAltreInfo.orarioOutDa}"
											label="{$_('page.locations.detail.employees_exit_time_from')}"
											type="time"
										/>
										<Input
											bind:value="{$data.sedeAltreInfo.orarioOutA}"
											label="{$_('page.locations.detail.employees_exit_time_to')}"
											type="time"
										/>
									</div>
									<div class="gap-y-2 md:gap-x-8 md:px-4 grid grid-cols-1">
										<Input label="{$_('page.locations.detail.other_times')}" bind:value="{$data.sedeAltreInfo.altriOrari}" />
										<div class="w-full">
											<Input
												input$readonly
												on:click="{() => surface.setOpen(true)}"
												label="{$_('page.locations.detail.how_overtime_hours_handled')}"
												value="{$data.sedeAltreInfo.gestioneOreStraordinarie}"
												class="w-full"
											/>
											<MenuSurface bind:this="{surface}" anchorCorner="BOTTOM_LEFT">
												<List>
													<Item
														on:SMUI:action="{() => {
															$data.sedeAltreInfo.gestioneOreStraordinarie = 'Possono essere recuperate (es.: Banca ore)';
															surface.setOpen(false);
															gestioneOreStraordinarieAltro = '';
														}}"
													>
														<Text>{$_('page.locations.detail.how_overtime_hours_handled_recovered_option')}</Text>
													</Item>
													<Item
														on:SMUI:action="{() => {
															$data.sedeAltreInfo.gestioneOreStraordinarie = 'Vengono pagate in busta paga';
															surface.setOpen(false);
															gestioneOreStraordinarieAltro = '';
														}}"
													>
														<Text>{$_('page.locations.detail.how_overtime_hours_handled_paid_option')}</Text>
													</Item>
													<Item
														on:SMUI:action="{() => {
															$data.sedeAltreInfo.gestioneOreStraordinarie =
																"Non è prevista liquidazione delle ore eccedenti l'orario di lavoro";
															surface.setOpen(false);
															gestioneOreStraordinarieAltro = '';
														}}"
													>
														<Text>{$_('page.locations.detail.how_overtime_hours_handled_no_liquidation_option')}</Text>
													</Item>
													<Separator />
													<div class="flex mx-4">
														<Input
															bind:value="{gestioneOreStraordinarieAltro}"
															label="{$_('page.locations.detail.how_overtime_hours_handled_other_option')}"
															class="w-full ml-2"
														/>
													</div>
												</List>
											</MenuSurface>
										</div>
									</div>
									<div class="gap-y-2 md:grid-cols-2 md:gap-x-8 md:px-4 md:pt-2 grid grid-cols-1">
										<Input
											label="{$_('page.locations.detail.mobility_management_annual_budget')}"
											type="number"
											input$min="0.0"
											input$step="0.1"
											bind:value="{$data.sedeAltreInfo.budgetAnnoEconomicoMobilità}"
										/>
										<Input
											label="{$_('page.locations.detail.number_mobility_management_human_resources')}"
											bind:value="{$data.sedeAltreInfo.risorseUmaneDedicateMobilità}"
											on:keypress="{isNumeric}"
										/>
										<FormField>
											<Checkbox bind:checked="{$data.sedeAltreInfo.sabatoLavorativo}" />
											<span slot="label">{$_('page.locations.detail.saturday_working_day')}</span>
										</FormField>
										<FormField>
											<Checkbox bind:checked="{$data.sedeAltreInfo.domenicaLavorativo}" />
											<span slot="label">{$_('page.locations.detail.sunday_working_day')}</span>
										</FormField>
									</div>
								</Content>
							</Panel>
							<Panel>
								<Header>{$_('page.locations.detail.company_communications_tools')}</Header>
								<Content>
									<div class="gap-y-2 md:grid-cols-2 md:gap-x-8 md:gap-y-0 md:px-4 lg:grid-cols-4 grid grid-cols-1">
										<FormField>
											<Checkbox bind:checked="{$data.sedeStrumentiComunicazione.volantiniBrochure}" />
											<span slot="label">{$_('page.locations.detail.flyers/brochures')}</span>
										</FormField>
										<FormField>
											<Checkbox bind:checked="{$data.sedeStrumentiComunicazione.affissioni}" />
											<span slot="label">{$_('page.locations.detail.postings_in_spaces')}</span>
										</FormField>
										<FormField>
											<Checkbox bind:checked="{$data.sedeStrumentiComunicazione.rubricaGiornaleAzienda}" />
											<span slot="label">{$_('page.locations.detail.business_journal_rubric')}</span>
										</FormField>
										<FormField>
											<Checkbox bind:checked="{$data.sedeStrumentiComunicazione.emailAllUser}" />
											<span slot="label">{$_('page.locations.detail.email_all_user')}</span>
										</FormField>
										<FormField>
											<Checkbox bind:checked="{$data.sedeStrumentiComunicazione.campagneSocial}" />
											<span slot="label">{$_('page.locations.detail.social_campaigns')}</span>
										</FormField>
										<FormField>
											<Checkbox bind:checked="{$data.sedeStrumentiComunicazione.newsletter}" />
											<span slot="label">{$_('page.locations.detail.newsletter')}</span>
										</FormField>
										<FormField>
											<Checkbox bind:checked="{$data.sedeStrumentiComunicazione.sitoIntranet}" />
											<span slot="label">{$_('page.locations.detail.intranet')}</span>
										</FormField>
										<FormField>
											<Checkbox bind:checked="{$data.sedeStrumentiComunicazione.altro}" />
											<span slot="label">{$_('global.other')}</span>
										</FormField>
									</div>
								</Content>
							</Panel>
						{/if}
					</Accordion>
				</div>
			</Card>
			<div class="pt-4">
				<ParagrafiAccessibilitaPSCL 
					{reteStradaleBoxes}
					{ciclabilitaBoxes}
					{trasportoPubblicoBoxes}
					{mobilitaCondivisaBoxes}/>
			</div>
		</div>
	{/if}
</Card>
